import React, { useEffect, useState } from "react";
import { PackageCheck, Copy, Printer, Check, Trophy, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtNOK, fmtPct, fmtDate, fileSize } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton, StatusChip } from "@/components/common";

function Block({ title, children, testId }) {
  return (
    <section data-testid={testId}>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </section>
  );
}

export const WonDialog = ({ opportunityId, open, onOpenChange }) => {
  const [h, setH] = useState(null);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setH(null);
    setError(null);
    api.get(`/opportunities/${opportunityId}/handover`).then((r) => setH(r.data)).catch((e) => setError(errMsg(e)));
  }, [open, opportunityId]);

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/opportunities/${opportunityId}/handover/send`);
      toast.success(`Order ${r.data.order_no} created in Visma`, { description: "Demo integration - no data left the system." });
      const fresh = await api.get(`/opportunities/${opportunityId}/handover`);
      setH(fresh.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  const copy = async () => {
    if (!h) return;
    const lines = [
      `HANDOVER - ${h.account?.name} - ${h.opportunity?.name}`,
      `Value: ${fmtNOK(h.totals?.total_sales)} NOK excl. VAT (GM ${fmtPct(h.totals?.gm_pct)})`,
      `Quote: v${h.quote?.version || "-"} ${h.quote?.status || ""}`,
      "",
      "PRODUCTS:",
      ...h.products.map((p) => `- ${p.qty} x ${p.name} (${p.manufacturer}) [${p.group}]`),
      "",
      "SERVICES:",
      ...h.services.map((p) => `- ${p.qty} ${p.unit} ${p.name}`),
      "",
      "ASSUMPTIONS:",
      ...(h.assumptions || []).map((a) => `- ${a}`),
      "",
      "TIMELINE:",
      ...(h.timeline || []).map((t) => `- ${t.phase}: ${t.duration}`),
      "",
      "OPEN RISKS:",
      ...(h.open_risks || []).map((r) => `- ${r}`),
      "",
      `Contacts: ${(h.contacts || []).map((c) => `${c.name} (${c.role}) ${c.email}`).join("; ")}`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Handover summary copied");
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const o = h?.opportunity;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="won-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl"><span className="flex h-8 w-8 items-center justify-center rounded-full bg-[hsl(var(--status-ok)/0.12)] text-[hsl(var(--status-ok))]"><Trophy className="h-4 w-4" /></span> Won - handover to delivery</DialogTitle>
          <DialogDescription>Everything the project team needs, collected automatically from the opportunity. Nothing to retype.</DialogDescription>
        </DialogHeader>
        {error && <div className="rounded-xl border border-[hsl(var(--status-bad)/0.3)] bg-[hsl(var(--status-bad)/0.06)] px-4 py-3 text-sm">{error}</div>}
        {!h && !error && <div className="space-y-3"><Skeleton className="h-16" /><Skeleton className="h-40" /><Skeleton className="h-24" /></div>}
        {h && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 rounded-2xl border bg-muted/30 p-4 text-sm sm:grid-cols-4" data-testid="handover-summary">
              <div><div className="text-xs text-muted-foreground">Customer</div><div className="font-medium">{h.account?.name}</div><div className="text-xs text-muted-foreground">{h.account?.city}{h.account?.city ? ", " : ""}{h.account?.country}</div></div>
              <div><div className="text-xs text-muted-foreground">Order value</div><div className="font-semibold tabular" data-testid="handover-total">{fmtNOK(h.totals?.total_sales)} NOK</div><div className="text-xs text-muted-foreground">GM {fmtPct(h.totals?.gm_pct)}</div></div>
              <div><div className="text-xs text-muted-foreground">Accepted quote</div><div className="font-medium">{h.quote ? `v${h.quote.version}` : "–"} {h.quote && <StatusChip status={h.quote.status} className="ml-1" />}</div><div className="text-xs text-muted-foreground">{h.quote?.sent_at ? `sent ${fmtDate(h.quote.sent_at)}` : ""}</div></div>
              <div><div className="text-xs text-muted-foreground">Lead time</div><div className="font-medium tabular">{h.totals?.max_lead_time_weeks || 0} weeks</div><div className="text-xs text-muted-foreground">longest supplier lead</div></div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Block title={`Products (${h.products.length})`} testId="handover-products">
                <ul className="space-y-1.5 text-sm">
                  {h.products.map((p) => <li key={p.id} className="flex items-baseline justify-between gap-3"><span className="min-w-0 truncate"><span className="tabular font-medium">{p.qty}×</span> {p.name} <span className="text-xs text-muted-foreground">{p.manufacturer}</span></span><span className="shrink-0 text-xs text-muted-foreground">{p.lead_time_weeks}w</span></li>)}
                  {h.products.length === 0 && <li className="text-muted-foreground">No hardware.</li>}
                </ul>
              </Block>
              <Block title={`Services (${h.services.length})`} testId="handover-services">
                <ul className="space-y-1.5 text-sm">
                  {h.services.map((p) => <li key={p.id}><span className="tabular font-medium">{p.qty} {p.unit}</span> {p.name}</li>)}
                  {h.services.length === 0 && <li className="text-muted-foreground">No services.</li>}
                </ul>
              </Block>
              <Block title="Contacts" testId="handover-contacts">
                <ul className="space-y-1.5 text-sm">{(h.contacts || []).map((c) => <li key={c.id}><span className="font-medium">{c.name}</span> <span className="text-muted-foreground">{c.role}{c.email ? ` · ${c.email}` : ""}{c.phone ? ` · ${c.phone}` : ""}</span></li>)}</ul>
              </Block>
              <Block title="Timeline" testId="handover-timeline">
                <ul className="space-y-1.5 text-sm">{(h.timeline || []).map((t, i) => <li key={i} className="flex justify-between gap-3"><span>{t.phase}</span><span className="tabular text-muted-foreground">{t.duration}</span></li>)}{(h.timeline || []).length === 0 && <li className="text-muted-foreground">{h.delivery_expectations || "No timeline in quote yet."}</li>}</ul>
              </Block>
              <Block title="Assumptions from the quote" testId="handover-assumptions">
                <ul className="list-disc space-y-1 pl-4 text-sm">{(h.assumptions || []).map((a, i) => <li key={i}>{a}</li>)}{(h.assumptions || []).length === 0 && <li className="list-none text-muted-foreground">None recorded.</li>}</ul>
              </Block>
              <Block title="Open risks" testId="handover-risks">
                <ul className="space-y-1 text-sm">{(h.open_risks || []).map((r, i) => <li key={i} className="flex gap-2"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-warn))]" />{r}</li>)}{(h.open_risks || []).length === 0 && <li className="text-muted-foreground">No open risks.</li>}</ul>
              </Block>
            </div>

            {h.files?.length > 0 && (
              <Block title={`Files (${h.files.length})`} testId="handover-files">
                <ul className="flex flex-wrap gap-2 text-xs">{h.files.map((f) => <li key={f.id} className="rounded-full border bg-white px-2.5 py-1">{f.name} <span className="text-muted-foreground">{fileSize(f.size)}</span></li>)}</ul>
              </Block>
            )}

            <div className="flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="handover-actions">
              <div className="text-sm">
                <div className="flex items-center gap-2 font-medium"><PackageCheck className="h-4 w-4 text-muted-foreground" /> Create order in Visma <span className="rounded-full border px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-muted-foreground">demo</span></div>
                <div className="text-xs text-muted-foreground">{o?.visma_order_no ? <span className="inline-flex items-center gap-1 text-[hsl(var(--status-ok))]"><Check className="h-3.5 w-3.5" /> Order {o.visma_order_no} created {fmtDate(o.handover_sent_at)}</span> : "Sends products, services, customer and contacts to the ERP. Simulated in this demo."}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" className="gap-1.5" onClick={copy} data-testid="handover-copy-button"><Copy className="h-3.5 w-3.5" /> Copy summary</Button>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => window.print()} data-testid="handover-print-button"><Printer className="h-3.5 w-3.5" /> Print</Button>
                {h.quote?.token && <Button variant="outline" size="sm" className="gap-1.5" asChild><a href={`/q/${h.quote.token}`} target="_blank" rel="noreferrer" data-testid="handover-open-quote-link"><ExternalLink className="h-3.5 w-3.5" /> Quote</a></Button>}
                <Button size="sm" disabled={sending || !!o?.visma_order_no} onClick={send} className="gap-1.5 bg-primary text-white hover:bg-[#ff8e76]" data-testid="handover-send-visma-button"><PackageCheck className="h-3.5 w-3.5" /> {o?.visma_order_no ? "Sent to Visma" : sending ? "Creating order..." : "Send to Visma"}</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WonDialog;
